import { createMessageDraft, type MessageDraftSession } from "../../lib/messageDraft";
// src/components/messages/MessagesPage.tsx
//
// TRAIN-MOBILE · the "Messages" surface — an SMS inbox that feels like
// Slack/iMessage instead of a drawer tab.
//
//   MOBILE  (< md): conversation list; tapping a row slides the thread in as
//     a FULL-SCREEN layer (MUI Slide, left). Three ways back, all equivalent:
//     the app-bar back arrow, the browser back button (selection rides the
//     URL via App's handleNavigate/pushState), and a swipe-right gesture that
//     starts in the left 24px edge zone — the layer follows the finger
//     (translateX) and a >70px drag completes the pop.
//
// ⚠️ THE DRAG TRANSFORM IS NOT ON THE <Slide> CHILD, DELIBERATELY.
//   v1 of this file set the drag translate through `sx` on the Box that is
//   Slide's direct child. Slide writes `transform` as an INLINE style on that
//   same node, and an sx-generated class can never beat an inline style: an
//   R2 reviewer measured the live node at dragX=40 and got
//   INLINE_TRANSFORM="none" COMPUTED="none". The advertised finger-follow had
//   never moved a pixel — the screen simply vanished at 70px with no feedback.
//   The interactive layer is now an INNER wrapper (`messages-thread-drag`)
//   that Slide does not own, and its transform is written as an inline style
//   so nothing can outrank it. MessagesPageSwipe.test.tsx measures that the
//   transform actually changes mid-drag; existence assertions cannot see this
//   class of bug.
//
// The gesture is bound with NATIVE listeners (not React's onTouch* props)
// because React 18 attaches delegated touchstart/touchmove at the root as
// PASSIVE — preventDefault() from a React handler is a no-op. We need it: the
// left 24px is also iOS Safari's own back-swipe zone, and without it a real
// edge swipe fires browser-back AND this pop (a double pop).
//   DESKTOP (md+): two-pane — list left (360px), selected thread right.
//
// The thread itself is ClientMessagesPanel REUSED (showHeader=false on
// mobile where the app bar already names the client) — one thread
// implementation everywhere, per the no-fork rule. All sends therefore go
// through the same gated /clients/{id}/sms endpoint; this page adds zero
// send paths.
//
// Permissions: mounted for manager/admin only by App (same D9 gating as the
// clients Messages tab). The API is the real boundary — agents get 403.

import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
  SwipeableDrawer,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Theme, alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ForumIcon from "@mui/icons-material/Forum";
import ClientMessagesPanel, { type ClientMessagesPanelHandle } from "../clients/ClientMessagesPanel";
import LogActivityDialog from "../clients/LogActivityDialog";
import SendInvoiceModal from "../clients/SendInvoiceModal";
import UploadLeaseModal from "../clients/UploadLeaseModal";
import type { Agent, Client } from "../clients/types";
import { isMobileUserAgent, openDialer } from "./railLaunch";
import ClientRecordDialog from "../clients/ClientRecordDialog";
import ClientContextRail from "./ClientContextRail";
import ShortlistSearchModal from "./ShortlistSearchModal";
import GuestCardDialog from "../GuestCardDialog";
import type { ClientInfo } from "../../services/emailService";
import { supabase } from "../../lib/supabase";
import QueueRail from "./QueueRail";
import { refusalMessage } from "../../lib/api";
import DaySummary from "./DaySummary";
import WorkspaceTabs, { type WorkspaceTabId } from "./WorkspaceTabs";
import {
  buildQueue,
  CHECK_IN_ACTION,
  checkInDraft,
  queueCount,
  QUEUE_VIEWS,
  type QueueViewId,
  type QueueGroup,
} from "./clientQueue";
import PageHeaderTitle from "../PageHeaderTitle";
import {
  SmsConversation,
  fetchSmsConversations,
} from "../../services/clientSmsService";
import {
  fetchClientQueue,
  dismissClientQueueRow,
  type ClientQueueRow,
  type QueueAgentOption,
  type QueueScopeInfo,
} from "../../services/clientQueueService";
import { usePersistedState } from "../../hooks/usePersistedState";
import { useCoordinatedRefresh } from "../../hooks/useCoordinatedRefresh";

// Poll cadence for the inbox list. Slightly slower than the open thread's
// 15s — the list is a summary; the thread is where immediacy matters.
const LIST_POLL_MS = 20000;

// The queue is heavier server-side (joins five tables); poll it slower.
const QUEUE_POLL_MS = 45000;

// Swipe-back tuning (iOS-style interactive pop).
const EDGE_ZONE_PX = 24; // gesture must START in this left strip
const COMPLETE_DRAG_PX = 70; // horizontal travel that commits the pop

export function relativeTime(iso: string | null): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "now";
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

// --------------------------------------------------------------------------
// Legibility tokens. Measured, not eyeballed — see MessagesPageLegibility.test.
//
// The shipped values failed WCAG AA in ways nobody could see from the code:
//   · opted-out avatar   grey.500 + primary.contrastText → 2.68:1 BOTH modes
//     (below AA 4.5 and below even AA-large 3.0 — initials on a grey disc)
//   · unread pill        primary.main + white             → 4.47:1 dark
//   · accent-on-page     primary.main text on background  → 4.06:1 dark paper
// Every one of these is a filled accent or an accent text run, so they get
// two tokens rather than one and the gate measures the rendered node.
// --------------------------------------------------------------------------

/** Filled accent (pill, avatar disc): white text needs a darker fill in dark
 *  mode, where primary.main (#6366F1) only reaches 4.4:1. */
export function accentFill(theme: Theme): { bg: string; fg: string } {
  return {
    bg: theme.palette.mode === "dark" ? theme.palette.primary.dark : theme.palette.primary.main,
    fg: "#FFFFFF",
  };
}

/** Accent used as TEXT on the page/paper background. The inverse problem:
 *  primary.main is too dark against #0F172A / #1E293B, so dark mode takes
 *  primary.light. */
export function accentText(theme: Theme): string {
  return theme.palette.mode === "dark"
    ? theme.palette.primary.light
    : theme.palette.primary.main;
}

/** Muted avatar disc for opted-out clients — it must read as "off" without
 *  becoming unreadable. grey.700 + white is 5.6:1 in both modes. */
export function mutedFill(theme: Theme): { bg: string; fg: string } {
  return { bg: theme.palette.grey[700], fg: "#FFFFFF" };
}

// The record dialog's tabs. A rail tab request outside this list opens the
// default tab rather than an empty one.
const RECORD_TABS = ["overview", "timeline", "messages", "shortlist", "files"] as const;
type RecordTab = (typeof RECORD_TABS)[number];
function asRecordTab(tab: string | undefined): RecordTab | null {
  return (RECORD_TABS as readonly string[]).includes(tab ?? "") ? (tab as RecordTab) : null;
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0]!.toUpperCase())
      .slice(0, 2)
      .join("") || "?"
  );
}

export interface MessagesPageProps {
  viewerId?: string;
  /** Currently open thread (rides the URL — App owns pushState). */
  selectedClientId: string | null;
  /** Open a thread (clientId) or return to the list (null). App wires this
   * to handleNavigate so browser back/forward replays the transition. */
  onSelectClient: (clientId: string | null) => void;
  /** Open the full record through App's typed navigation controller. */
  onOpenClientRecord: (clientId: string, tab?: string) => void;
  /** The guest card's "connect Gmail" link — Settings > Integrations, as on Leads. */
  onNavigateToSettings?: () => void;
}

export default function MessagesPage({
  viewerId,
  selectedClientId,
  onSelectClient,
  onOpenClientRecord,
  onNavigateToSettings,
}: MessagesPageProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  // TRAIN-2b: the client-context rail. Collapsed state persists — an operator
  // who wants a wider thread should not have to re-collapse it every visit.
  const [railCollapsed, setRailCollapsed] = usePersistedState<boolean>(
    "messages.railCollapsed",
    false
  );
  const toggleRail = React.useCallback(() => {
    setRailCollapsed((prev) => !prev);
  }, []);
  const [conversations, setConversations] = useState<SmsConversation[] | null>(null);
  // The client action queue is shared by desktop and phone.
  // `queue === null` = never loaded; `queueError` set = the FETCH failed.
  // The two are kept separate so a failed fetch can NEVER render as an
  // empty-but-calm queue (the estate's silent-noop rule): the calm empty copy
  // is reachable only from a successful response with zero rows.
  const [queue, setQueue] = useState<{
    clients: ClientQueueRow[];
    generatedAt: string;
    scope?: QueueScopeInfo;
    agents?: QueueAgentOption[];
    dismissalAvailable?: boolean;
  } | null>(null);
  // Ch78 · WHOSE clients. Defaults to the signed-in user's; managers/admins
  // may switch (the server decides who may; a failed roster read still
  // permits Me/Everyone for authorized managers).
  const [queueAgent, setQueueAgent] = usePersistedState<string>("messages.queueAgent", "mine");
  const scopeIdentity = useRef({ agent: queueAgent, generation: 0 });
  // Keep stale in-flight reads from resurrecting an optimistic dismissal.
  const dismissals = useRef(new Map<string, { confirmedAt?: number }>());
  const dismissalVersion = useRef(0);
  const dismissFailure = useRef(false);
  if (scopeIdentity.current.agent !== queueAgent) {
    scopeIdentity.current = { agent: queueAgent, generation: scopeIdentity.current.generation + 1 };
    dismissals.current.clear();
    dismissFailure.current = false;
  }
  const [queueError, setQueueError] = useState<string | null>(null);
  // Grant's three Messages filters; stage grouping lives in Leads.
  const [queueView, setQueueView] = useState<QueueViewId>("needs_reply");
  const [queueGroup, setQueueGroup] = useState<QueueGroup | null>(null);
  const [queueSearch, setQueueSearch] = useState("");
  // Ch78 · a drafted message waiting for the composer (the "Check in" action).
  const messageDrafts = useRef(new Map<string, MessageDraftSession>());
  const draftOwner = useRef(viewerId);
  if (draftOwner.current !== viewerId) {
    draftOwner.current = viewerId;
    messageDrafts.current.clear();
  }
  const draftFor = (id: string) => {
    let session = messageDrafts.current.get(id);
    if (!session) {
      session = createMessageDraft();
      messageDrafts.current.set(id, session);
    }
    return session;
  };
  // Ch78 · "Open full client record" opens THE COMPLETE RECORD as a modal
  // here, instead of leaving for the Leads tab. The old destination stays
  // one click away inside the dialog ("Open in Leads").
  const [recordTaskView, setRecordTaskView] = useState(false);
  const [recordClientId, setRecordClientId] = useState<string | null>(null);
  // PKT-0873 · the tab the caller asked for. The rail asks for "timeline" when
  // it falls back to the record; this page used to drop the tab on the floor.
  const [recordTab, setRecordTab] = useState<RecordTab | null>(null);
  const openRecord = useCallback((id: string, tab?: string) => {
    setRecordTab(asRecordTab(tab));
    setRecordClientId(id);
  }, []);
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTabId>("conversation");
  // Rail "Send list" opens the search modal HERE rather than navigating to the
  // client record. The rail stays presentational; the page owns the modals.
  const [railShortlistFor, setRailShortlistFor] = useState<{
    id: string;
    firstName: string;
  } | null>(null);
  const [shortlistRefreshKey, setShortlistRefreshKey] = useState(0);
  // PKT-0866 · rail "Send Guest Card" opens the SAME GuestCardDialog Leads
  // does (ClientList `case "send_guest_card"`), fed the same full client row
  // (Leads lists clients with select("*")). Until PR #445's scaffold was split
  // here, this key opened the shortlist search modal.
  const [railGuestCardClient, setRailGuestCardClient] = useState<ClientInfo | null>(null);
  // PKT-0873 · the rest of the rail runs the SAME dialogs Leads runs
  // (ClientList handleQuickAction): Upload Lease -> UploadLeaseModal, Send
  // Invoice -> SendInvoiceModal, Call -> tel: + LogActivityDialog. Each is fed
  // the full client row and, for lease/invoice, the assigned agent's profile —
  // the same `currentAgent` Leads finds in its agents list.
  const [railLease, setRailLease] = useState<{ client: Client; agent: Agent | null } | null>(null);
  const [railInvoice, setRailInvoice] = useState<{ client: Client; agent: Agent | null } | null>(null);
  const [railLog, setRailLog] = useState<{
    client: Client;
    type: string;
    meta?: Record<string, unknown>;
  } | null>(null);
  // The open thread's composer: "Send Text" focuses it, and a list saved from
  // the rail puts its link in its draft.
  const composerHandle = useRef<ClientMessagesPanelHandle | null>(null);
  const visibleComposerClient = useRef(selectedClientId);
  useLayoutEffect(() => {
    visibleComposerClient.current = selectedClientId;
  }, [selectedClientId]);
  // The client a rail read was started for; a thread switch mid-read clears
  // it so a late row can never open a dialog for the wrong client.
  const railRequestFor = useRef<string | null>(null);
  useEffect(() => {
    railRequestFor.current = null;
  }, [selectedClientId]);
  // Interactive swipe-back: the layer's live translateX while dragging.
  const [dragX, setDragX] = useState(0);
  const dragRef = useRef<{ startX: number; startY: number; active: boolean }>({
    startX: 0,
    startY: 0,
    active: false,
  });
  // Native listeners cannot read React state, and the commit threshold is
  // checked inside touchend — so dragX is mirrored into a ref.
  const dragXRef = useRef(0);
  const dragNodeRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (scopeIdentity.current.agent !== queueAgent) return;
    const generation = scopeIdentity.current.generation;
    try {
      const rows = await fetchSmsConversations(queueAgent);
      if (generation === scopeIdentity.current.generation) setConversations(rows);
    } catch (err: any) {
      // The queue owns visible load errors; preserve the last conversation summary.
    }
  }, [queueAgent]);
  const requestConversationRefresh = useCoordinatedRefresh(load, {
    pollMs: LIST_POLL_MS,
    requestOnMount: false,
  });

  const loadQueue = useCallback(async () => {
    if (scopeIdentity.current.agent !== queueAgent) return;
    const generation = scopeIdentity.current.generation;
    const readVersion = dismissalVersion.current;
    try {
      const res = await fetchClientQueue(queueAgent);
      if (generation !== scopeIdentity.current.generation) return;
      // Only a read STARTED after success may reconcile that dismissal.
      dismissals.current.forEach((entry, id) => {
        if (entry.confirmedAt !== undefined && entry.confirmedAt <= readVersion) dismissals.current.delete(id);
      });
      const clients = res.clients.filter(row => !dismissals.current.has(row.id));
      setQueue({ clients, generatedAt: res.generated_at, scope: res.scope, agents: res.agents, dismissalAvailable: res.dismissal_available });
      if (!dismissFailure.current) setQueueError(null);
    } catch (err: any) {
      // A failed refresh keeps the last good queue on screen but STILL sets
      // the error — the operator must know the list may be stale. A cold
      // failure (no data yet) renders the error state, never the empty state.
      if (generation === scopeIdentity.current.generation && !dismissFailure.current) setQueueError(err?.message || "Failed to load the client queue");
    }
  }, [queueAgent]);
  const requestQueueRefresh = useCoordinatedRefresh(loadQueue, {
    pollMs: QUEUE_POLL_MS,
    requestOnMount: false,
    enabled: true,
  });

  const dismissQueueClient = useCallback(async (clientId: string) => {
    const client = queue?.clients.find((row) => row.id === clientId);
    if (!client || dismissals.current.has(clientId)) return;
    const generation = scopeIdentity.current.generation;
    const index = queue!.clients.indexOf(client);
    const entry: { confirmedAt?: number } = {};
    dismissals.current.set(clientId, entry);
    dismissalVersion.current += 1;
    dismissFailure.current = false;
    setQueueError(null);
    setQueue(current => current && ({ ...current, clients: current.clients.filter(row => row.id !== clientId) }));
    try {
      await dismissClientQueueRow(clientId, {
        last_inbound_id: client.last_inbound_id ?? null,
        last_inbound_tie_fingerprint: client.last_inbound_tie_fingerprint ?? null,
        last_inbound_at: client.last_inbound_at ?? null,
        move_in_timeline: client.move_in_timeline ?? null,
        move_in_date: client.move_in_date ?? null,
        move_in_near: Boolean(client.move_in_near),
      }, queueAgent);
      if (generation !== scopeIdentity.current.generation) return;
      entry.confirmedAt = ++dismissalVersion.current;
      // The next normal poll reconciles; no void refresh is pretended awaitable.
    } catch (err: any) {
      if (generation !== scopeIdentity.current.generation) return;
      dismissals.current.delete(clientId);
      setQueue(current => {
        if (!current || current.clients.some(row => row.id === clientId)) return current;
        const clients = [...current.clients];
        clients.splice(Math.min(index, clients.length), 0, client);
        return { ...current, clients };
      });
      dismissFailure.current = true;
      const reason = refusalMessage(err) || err?.message;
      setQueueError(`Could not dismiss this follow-up${reason ? `: ${reason}` : ""}`);
    }
  }, [queue, queueAgent]);

  useEffect(() => {
    // PR #454 review Q2: selectedClientId is deliberately NOT a dependency —
    // row selection must not refetch (the join per click) and, ranking being
    // live, must never re-sort the rail out from under the operator's cursor.
    // Unlike the thread list, opening a queue row changes nothing server-side
    // that the rail displays; the 45s poll picks changes up.
    requestQueueRefresh();
    // Ch78 · switching WHOSE clients is a new list, not a refresh of this one.
  }, [requestQueueRefresh, queueAgent]);

  // Initial load + refetch whenever the open-thread selection changes:
  // opening a thread marks it read server-side, so returning to the list
  // (or opening on desktop) refetches and the unread pill clears.
  useEffect(() => {
    requestConversationRefresh();
  }, [requestConversationRefresh, selectedClientId, queueAgent]);

  const selected =
    conversations?.find((c) => c.client_id === selectedClientId) ?? null;
  const selectedQueueClient =
    queue?.clients.find((c) => c.id === selectedClientId) ?? null;
  const queueClientName = selectedQueueClient
    ? [selectedQueueClient.first_name, selectedQueueClient.last_name]
        .filter(Boolean)
        .join(" ")
        .trim() || "Unnamed client"
    : null;
  const selectedName = selected?.client_name ?? queueClientName ?? "Messages";

  // ---- queue derivations (pure; buildQueue is fixture-tested) -------------
  // nowIso comes from the backend's own generated_at so ordering is
  // deterministic against the data's timestamps, per the route contract.
  const queueNowIso = queue?.generatedAt ?? "";
  const searchNeedle = queueSearch.trim().toLowerCase();
  // PKT-0693 S2 · no scope. Nobody is hidden; placement does the work the mode
  // used to do. The search box is the only narrowing left, and it never reorders.
  const scopedClients = queue?.clients ?? [];
  const queueClients = React.useMemo(() => {
    const all = scopedClients;
    if (!searchNeedle) return all;
    return all.filter((c) =>
      [c.first_name, c.last_name].filter(Boolean).join(" ").toLowerCase().includes(searchNeedle)
    );
  }, [scopedClients, searchNeedle]);
  const queueRows = React.useMemo(
    () => (queue ? buildQueue(queueClients, queueView, queueNowIso).filter(row => queueGroup ? row.group === queueGroup : true) : []),
    [queue, queueClients, queueView, queueNowIso, queueGroup]
  );
  const queueCounts = React.useMemo(() => {
    // PR #454 review Q3: badges are WORKLOAD numbers, so they count the
    // UNFILTERED list — typing in the search box narrows the rows, never
    // rewrites what "Needs action: 7" means.
    // The SCOPE is part of what the number means ("Needs action: 7" among
    // engaged clients), so counts follow the scope, never the search.
    const counts: Partial<Record<QueueViewId, number>> = {};
    if (!queue) return counts;
    for (const v of QUEUE_VIEWS) {
      counts[v.id] = queueCount(buildQueue(scopedClients, v.id, queueNowIso));
    }
    return counts;
  }, [queue, scopedClients, queueNowIso]);

  const setDrag = useCallback((px: number) => {
    dragXRef.current = px;
    setDragX(px);
  }, []);

  /** Always leaves the gesture idle AND the layer at rest. Every exit path
   *  from the gesture goes through here — the v1 bug was that two of them
   *  (touchcancel, and the early-return in touchend) left `dragX` at its
   *  partial value, stranding a half-open panel on screen forever. */
  const resetDrag = useCallback(() => {
    dragRef.current.active = false;
    setDrag(0);
  }, [setDrag]);

  const closeThread = useCallback(() => {
    resetDrag();
    onSelectClient(null);
  }, [onSelectClient, resetDrag]);

  // --- swipe-right-from-left-edge to pop (mobile thread layer) -------------
  // Bound natively, with {passive:false}, so preventDefault() actually lands.
  useEffect(() => {
    const node = dragNodeRef.current;
    if (!node) return;

    const inAppBar = (target: EventTarget | null) =>
      target instanceof Element && Boolean(target.closest(".MuiAppBar-root"));

    const onTouchStart = (e: TouchEvent) => {
      // HOLE 2: a second finger mid-drag used to re-run this and recompute
      // `active` from touches[0] — now far from the edge — flipping active to
      // false, after which touchend early-returned and dragX never reset.
      // A multi-touch gesture is not a back-swipe: abandon cleanly instead.
      if (e.touches.length > 1) {
        resetDrag();
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      const active = t.clientX <= EDGE_ZONE_PX && !inAppBar(e.target);
      dragRef.current = { startX: t.clientX, startY: t.clientY, active };
      // HOLE 3: the left 24px is ALSO iOS Safari's back-swipe zone. Claiming
      // the gesture here is what stops browser-back firing alongside our pop.
      // Excluded inside the app bar so the back IconButton (edge="start", so
      // its left half sits inside the strip) still takes taps.
      if (active && e.cancelable) e.preventDefault();
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!dragRef.current.active) return;
      const t = e.touches[0];
      if (!t) return;
      const dx = t.clientX - dragRef.current.startX;
      const dy = Math.abs(t.clientY - dragRef.current.startY);
      // mostly-vertical movement is a scroll, not a pop
      if (dx > 0 && dx > dy) {
        if (e.cancelable) e.preventDefault();
        setDrag(dx);
      }
    };

    const onTouchEnd = () => {
      const wasActive = dragRef.current.active;
      dragRef.current.active = false;
      if (wasActive && dragXRef.current > COMPLETE_DRAG_PX) {
        closeThread();
      } else {
        setDrag(0); // spring back — unconditionally, active or not
      }
    };

    // HOLE 1: touchcancel was not bound at all. An incoming call / system
    // gesture fires it INSTEAD of touchend, so `active` stayed true and dragX
    // kept its partial value forever.
    const onTouchCancel = () => resetDrag();

    node.addEventListener("touchstart", onTouchStart, { passive: false });
    node.addEventListener("touchmove", onTouchMove, { passive: false });
    node.addEventListener("touchend", onTouchEnd);
    node.addEventListener("touchcancel", onTouchCancel);
    return () => {
      node.removeEventListener("touchstart", onTouchStart);
      node.removeEventListener("touchmove", onTouchMove);
      node.removeEventListener("touchend", onTouchEnd);
      node.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [closeThread, resetDrag, setDrag, selectedClientId]);

  const totalUnread =
    conversations?.reduce((sum, c) => sum + c.unread_count, 0) ?? 0;

  const header = (
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      <PageHeaderTitle
        icon={
          <Badge badgeContent={totalUnread} color="primary" max={99}>
            <ForumIcon />
          </Badge>
        }
        title="Messages"
        titleVariant="h5"
        subtitle="Client SMS threads on the LocateNow number"
      />
    </Box>
  );

  // Ch78 · the complete-record modal, mounted in BOTH layouts.
  const recordDialog = (
        <ClientRecordDialog
          clientId={recordClientId}
          open={recordClientId !== null}
          initialTab={recordTaskView ? "timeline" : recordTab ?? "overview"}
          onClose={() => { setRecordClientId(null); setRecordTaskView(false); setRecordTab(null); }}
          consentState={recordClientId === selectedClientId ? selectedQueueClient?.consent_state ?? null : null}
          consentSource={recordClientId === selectedClientId ? selectedQueueClient?.consent_source ?? null : null}
          onOpenInLeads={(id) => {
            setRecordClientId(null);
            onOpenClientRecord(id);
          }}
        />
  );

  const queueList = <>
          {(
            queueError && queue === null ? (
              // A failed fetch is an ERROR, never an empty-but-calm queue.
              // Ch78 · and an error the operator can ACT on: the 45 s poll
              // will retry on its own, but "Load failed" with no button left
              // Grant staring at a red box (2026-09-07). The copy comes from
              // the API layer (NetworkError / TimeoutError / ApiError), which
              // now says what is knowable instead of the browser's two words.
              <Alert
                severity="error"
                data-testid="queue-error"
                sx={{ m: 1.5, alignItems: "center" }}
                action={
                  <Button
                    size="small"
                    color="inherit"
                    data-testid="queue-retry"
                    onClick={() => requestQueueRefresh()}
                  >
                    Retry
                  </Button>
                }
              >
                {queueError}
              </Alert>
            ) : (
              <>
                {queueError && (
                  <Alert
                    severity="warning"
                    data-testid="queue-stale-warning"
                    sx={{ mx: 1.5, mb: 0.5, py: 0 }}
                  >
                    {dismissFailure.current ? queueError : "Refresh failed — this list may be stale."}
                  </Alert>
                )}
                <QueueRail
                  rows={queueRows}
                  groupFilter={queueGroup}
                  onClearGroup={() => setQueueGroup(null)}
                  view={queueView}
                  onViewChange={(view) => { setQueueView(view); setQueueGroup(null); }}
                  selectedClientId={selectedClientId}
                  dismissalAvailable={Boolean(queue?.dismissalAvailable)}
                  onDismiss={dismissQueueClient}
                  onSelect={(id, action) => {
                    const c = queue?.clients.find((x) => x.id === id);
                    const session = draftFor(id);
                    if (action === CHECK_IN_ACTION && !session.getSnapshot().text && !session.getSnapshot().sending) {
                      session.update({ text: checkInDraft(c?.first_name) });
                    }
                    onSelectClient(id);
                    if (action === "Review task") { setRecordTaskView(true); setRecordClientId(id); }
                  }}
                  counts={queueCounts}
                  loading={queue === null}
                  search={queueSearch}
                  onSearchChange={setQueueSearch}
                  agent={queueAgent}
                  onAgentChange={(a) => {
                    setQueueAgent(a);
                    setQueueError(null);
                    setQueue(null); // loading state — never another agent's rows under this name
                  }}
                  agents={queue?.agents ?? []}
                  viewerId={viewerId}
                  agentScope={queue?.scope}
                />
              </>
            )
          )}
  </>;

  // ---------------------------------------------------------------- desktop
  // Mobile: the same rail in a right-side drawer. Opening pushes a same-URL
  // history entry so the phone's Back closes the DRAWER first, then the thread
  // (the thread's own open state rides App's URL).
  const [mobileRailOpen, setMobileRailOpen] = useState(false);
  const railHistoryPushed = React.useRef(false);
  const openMobileRail = React.useCallback(() => {
    window.history.pushState({ ...(window.history.state || {}), messagesRailDrawer: true }, "");
    railHistoryPushed.current = true;
    setMobileRailOpen(true);
  }, []);
  const closeMobileRail = React.useCallback(() => {
    if (railHistoryPushed.current) {
      railHistoryPushed.current = false;
      window.history.back(); // the popstate below closes it
      return;
    }
    setMobileRailOpen(false);
  }, []);
  useEffect(() => {
    const onPop = () => {
      railHistoryPushed.current = false;
      setMobileRailOpen(false);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  // A different (or no) thread never inherits an open drawer.
  useEffect(() => {
    setMobileRailOpen(false);
  }, [selectedClientId]);

  /**
   * Read the full client row (select("*"), the shape Leads hands its dialogs)
   * and, when asked, the assigned agent's profile, then run `open`. A failed
   * or empty read opens the record at the tab the rail's own fallback uses —
   * never a dead click. A thread switch mid-read drops the late result.
   */
  const withRailClient = (
    id: string,
    withAgent: boolean,
    open: (client: Client, agent: Agent | null) => void,
  ) => {
    railRequestFor.current = id;
    (async () => {
      let client: Client | null = null;
      let agent: Agent | null = null;
      try {
        const { data, error } = await supabase
          .from("clients")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (!error && data) client = data as Client;
      } catch {
        client = null;
      }
      if (client && withAgent && client.assigned_agent_id) {
        try {
          const { data, error } = await supabase
            .from("profiles")
            .select("id, full_name, email, avatar_url, role")
            .eq("id", client.assigned_agent_id)
            .maybeSingle();
          if (!error && data) agent = data as Agent;
        } catch {
          agent = null; // the dialogs fall back to the signed-in agent's name
        }
      }
      if (railRequestFor.current !== id) return;
      railRequestFor.current = null;
      if (!client) {
        openRecord(id, "timeline");
        return;
      }
      open(client, agent);
    })();
  };

  /**
   * "Send Text": the composer is already on screen, so focus it. On a phone
   * the rail is a drawer over the thread: close it SYNCHRONOUSLY first so its
   * focus trap lets go (and restores focus to the info button) BEFORE focus
   * moves, all inside the tap — iOS only raises the keyboard for a focus
   * that happens during the user's gesture.
   */
  const focusComposerFromRail = (variant?: "side" | "sheet") => {
    if (variant === "sheet") {
      flushSync(() => setMobileRailOpen(false));
      if (railHistoryPushed.current) {
        railHistoryPushed.current = false;
        window.history.back(); // pops the drawer's same-URL history entry
      }
    }
    composerHandle.current?.focusComposer();
  };

  // ONE rail wiring for both layouts: desktop renders it as a column, a phone
  // renders the same component in a drawer (variant="sheet"). Never a copy.
  const renderRail = (opts: {
    collapsed: boolean;
    onToggleCollapsed: () => void;
    variant?: "side" | "sheet";
    onHandled?: () => void;
  }) => (
    // clientId is non-null at every call site.
    <ClientContextRail
            key={`rail-${selectedClientId}`}
            shortlistRefreshKey={shortlistRefreshKey}
            clientId={selectedClientId as string}
            consentState={selectedQueueClient?.consent_state ?? null}
            consentSource={selectedQueueClient?.consent_source ?? null}
            collapsed={opts.collapsed}
            onToggleCollapsed={opts.onToggleCollapsed}
            variant={opts.variant}
            onOpenClientRecord={(id, tab) => openRecord(id, tab)}
            onClientChanged={load}
            onAction={(key) => {
              // Handled in place. Anything not listed returns false and falls
              // back to opening the full record, which is the pre-existing
              // behaviour — a new action key degrades, it never dead-ends.
              const id = selectedClientId as string;
              if (key === "sent_text") {
                focusComposerFromRail(opts.variant);
                return true;
              }
              if (key === "call") {
                // Leads' call: dial, and leave the log dialog waiting for the
                // operator's return. Nothing is written until they confirm.
                // The mobile launch keeps Leads' provenance tag (PKT-0410).
                withRailClient(id, false, (client) => {
                  const phone = client.phone || "";
                  setRailLog({
                    client,
                    type: "call_logged",
                    meta:
                      phone && isMobileUserAgent()
                        ? { launch_mode: "mobile_tel_handoff" }
                        : undefined,
                  });
                  if (phone) openDialer(phone);
                });
                opts.onHandled?.();
                return true;
              }
              if (key === "upload_lease") {
                withRailClient(id, true, (client, agent) => setRailLease({ client, agent }));
                opts.onHandled?.();
                return true;
              }
              if (key === "send_invoice") {
                withRailClient(id, true, (client, agent) => setRailInvoice({ client, agent }));
                opts.onHandled?.();
                return true;
              }
              if (key === "send_guest_card") {
                withRailClient(id, false, (client) =>
                  setRailGuestCardClient(client as unknown as ClientInfo),
                );
                opts.onHandled?.();
                return true;
              }
              if (key === "send_list") {
                const conv = (conversations || []).find(
                  (c) => c.client_id === selectedClientId,
                );
                // Queue-selected clients may have no SMS thread yet, so fall
                // back to the queue feed for the first name.
                const name =
                  conv?.client_name ||
                  selectedQueueClient?.first_name ||
                  "";
                setRailShortlistFor({
                  id: selectedClientId as string,
                  firstName: name.trim().split(/\s+/)[0] || "the client",
                });
                opts.onHandled?.();
                return true;
              }
              return false;
            }}
          />
  );
  const railShortlistModal = (
    <>
      {railShortlistFor && (
          <ShortlistSearchModal
            open
            clientId={railShortlistFor.id}
            clientFirstName={railShortlistFor.firstName}
            onClose={() => setRailShortlistFor(null)}
            onSaved={(shortlist) => {
              // PKT-0873 · the list's link goes into THIS thread's draft,
              // exactly as the composer's own shortlist button does. Draft
              // only: the human still reads it and presses Send.
              if (railShortlistFor.id === visibleComposerClient.current) {
                composerHandle.current?.insertShortlistLink(shortlist);
              }
              setRailShortlistFor(null);
              setShortlistRefreshKey((k) => k + 1);
              load();
            }}
          />
        )}
      {railGuestCardClient && (
          <GuestCardDialog
            open
            client={railGuestCardClient}
            onClose={() => setRailGuestCardClient(null)}
            onNavigateToSettings={onNavigateToSettings}
            onSuccess={() => {
              load();
            }}
          />
        )}
      {railLease && (
          <UploadLeaseModal
            open
            client={railLease.client}
            currentAgent={railLease.agent}
            onClose={() => setRailLease(null)}
            onSuccess={() => {
              load();
            }}
          />
        )}
      {railInvoice && (
          <SendInvoiceModal
            open
            client={railInvoice.client}
            currentAgent={railInvoice.agent}
            onClose={() => setRailInvoice(null)}
            onSuccess={() => {
              load();
            }}
          />
        )}
      {railLog && (
          <LogActivityDialog
            open
            client={railLog.client}
            initialActivityType={railLog.type}
            extraMetadata={railLog.meta}
            onClose={() => setRailLog(null)}
            onSuccess={() => {
              load();
            }}
          />
        )}
    </>
  );

  if (isDesktop) {
    return (
      <Paper
        data-testid="messages-desktop-layout"
        variant="outlined"
        sx={{
          display: "flex",
          height: "calc(100vh - 180px)",
          minHeight: 480,
          overflow: "hidden",
        }}
      >
        <Box
          data-testid="messages-pane-list"
          sx={{
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            // 🔴 THE RAIL SCALES WITH THE SCREEN.
            // These were flat pixel widths chosen for a ~1280px laptop. On
            // Grant's ~2000px display that left a 316px working list beside
            // ~1400px of empty detail pane — the pane with nothing in it took
            // four times the room of the pane with the work in it, and the
            // queue rows (name + why + three chips) were the thing paying for
            // it. The list now earns width as the viewport grows; the flat
            // width survives as the small-screen value.
            // NOTE the shape: a plain `width` FIRST, then breakpoint
            // overrides. An sx breakpoint OBJECT (`{ xs: 316, lg: 380 }`)
            // would compile to media queries only — including for `xs`, which
            // MUI writes as `@media (min-width:0px)`. jsdom does not apply
            // media rules, so `getComputedStyle(...).width` would read "" and
            // MessagesPageDesktop.test.tsx's existing "the list pane keeps its
            // declared column width" assertion would break for a reason that
            // has nothing to do with the layout. Writing the base value as an
            // unconditional declaration keeps that gate meaningful and still
            // lets real browsers take the wider values.
            width: 316,
            [theme.breakpoints.up("lg")]: {
              width: 380,
            },
            [theme.breakpoints.up("xl")]: {
              width: 460,
            },
            // QueueRail draws its own right border; only the thread list
            // needs the wrapper to provide one.
            borderRight:
              "none",
          }}
        >
          {queueList}
        </Box>
        <Box
          data-testid="messages-pane-thread"
          sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, minHeight: 0 }}
        >
          {selectedClientId ? (
            <>
              {selectedQueueClient && (
                <WorkspaceTabs
                  clientName={queueClientName || "Unnamed client"}
                  status={selectedQueueClient.status}
                  // daysInStage deliberately omitted: the queue feed has no
                  // status_changed_at, and mislabelling last-contact age as
                  // "stalled in stage" would be a lie. Wire it when the feed
                  // carries the real fact.
                  tabs={[{ id: "conversation", label: "Conversation" }]}
                  active={workspaceTab}
                  onChange={setWorkspaceTab}
                  onOpenFullRecord={() => openRecord(selectedClientId)}
                />
              )}
              <ClientMessagesPanel
                key={`${viewerId ?? "viewer"}:${selectedClientId}`}
                clientId={selectedClientId}
                clientName={selectedName}
                showHeader={!selectedQueueClient}
                draftSession={draftFor(selectedClientId)}
                handleRef={composerHandle}
              />
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "text.secondary",
                gap: 1,
              }}
            >
              <Box
                data-testid="messages-thread-empty"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  // Capped and centred: the copy is one short sentence and has
                  // no business spanning 1400px of empty pane.
                  maxWidth: 560,
                  width: "100%",
                  gap: 1,
                }}
              >
                {queue ? (
                  <DaySummary
                    rows={buildQueue(scopedClients, "needs_action", queueNowIso)}
                    onPick={(v, group) => { setQueueView(v); setQueueGroup(v === "needs_reply" ? null : group ?? null); }}
                  />
                ) : (
                  <>
                    <ForumIcon sx={{ fontSize: 48, opacity: 0.35 }} />
                    <Typography variant="body1">
                      Select a conversation to read the thread
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </Box>
        {selectedClientId && renderRail({ collapsed: railCollapsed, onToggleCollapsed: toggleRail })}
        {railShortlistModal}
        {recordDialog}
      </Paper>
    );
  }

  // ----------------------------------------------------------------- mobile
  return (
    <Box data-testid="messages-mobile-layout" sx={{ position: "relative" }}>
      {header}
      <Box sx={{ minHeight: 320 }}>{queueList}</Box>
      {/* pad the list past the floating bottom-nav pill */}
      <Box sx={{ height: "calc(env(safe-area-inset-bottom, 0px) + 90px)" }} />

      <Slide direction="left" in={Boolean(selectedClientId)} mountOnEnter unmountOnExit>
        <Box
          data-testid="messages-thread-layer"
          sx={{
            position: "fixed",
            // NOT `inset: 0` — spelled out so the gate can assert each edge
            // resolves to 0px. A reviewer moved this layer to `left: 200vw`
            // and the whole suite stayed green.
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: theme.zIndex.appBar + 2, // above the bottom-nav pill
            bgcolor: "background.default",
          }}
        >
          {/* The interactive layer. Slide owns the TRANSFORM of its own
              child (inline style), so the finger-follow lives one level in,
              on a node nothing else writes transform to. */}
          <Box
            ref={dragNodeRef}
            data-testid="messages-thread-drag"
            style={{
              // inline, deliberately: an sx class cannot outrank an inline
              // transform, and that is exactly how v1 died.
              transform: `translateX(${dragX}px)`,
              transition: dragRef.current.active ? "none" : "transform 180ms ease-out",
            }}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              bgcolor: "background.default",
              // let vertical scrolling through; horizontal is ours
              touchAction: "pan-y",
              // keep the composer clear of the home indicator
              pb: "env(safe-area-inset-bottom, 0px)",
            }}
          >
          <AppBar
            position="static"
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: "blur(12px)",
              color: "text.primary",
              borderBottom: `1px solid ${theme.palette.divider}`,
              pt: "env(safe-area-inset-top, 0px)",
            }}
          >
            <Toolbar sx={{ gap: 1, minHeight: 56 }}>
              <IconButton
                edge="start"
                aria-label="Back to conversations"
                data-testid="thread-back-button"
                onClick={closeThread}
              >
                <ArrowBackIcon />
              </IconButton>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  fontWeight: 700,
                  bgcolor: accentFill(theme).bg,
                  color: accentFill(theme).fg,
                }}
              >
                {initials(selectedName)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" fontWeight={700} noWrap>
                  {selectedName}
                </Typography>
                {selected?.client_phone && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {selected.client_phone}
                  </Typography>
                )}
              </Box>
              <IconButton
                edge="end"
                aria-label="Client details"
                data-testid="thread-rail-button"
                onClick={openMobileRail}
                sx={{ ml: "auto" }}
              >
                <InfoOutlinedIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
            {selectedClientId && (
              <ClientMessagesPanel
                key={`${viewerId ?? "viewer"}:${selectedClientId}`}
                clientId={selectedClientId}
                clientName={selectedName}
                draftSession={draftFor(selectedClientId)}
                handleRef={composerHandle}
                showHeader={false}
              />
            )}
          </Box>
          </Box>
        </Box>
      </Slide>
      <SwipeableDrawer
        anchor="right"
        open={mobileRailOpen && Boolean(selectedClientId)}
        onOpen={() => undefined}
        onClose={closeMobileRail}
        disableSwipeToOpen
        PaperProps={{ sx: { width: "min(360px, 92vw)" }, "data-testid": "messages-rail-drawer" } as any}
      >
        {selectedClientId && renderRail({
          collapsed: false,
          onToggleCollapsed: closeMobileRail,
          variant: "sheet",
          // An action that opens a modal closes the drawer underneath it.
          onHandled: closeMobileRail,
        })}
      </SwipeableDrawer>
      {railShortlistModal}
      {recordDialog}
    </Box>
  );
}
